import React, { useMemo } from 'react';
import { Target, Info } from 'lucide-react';
import { CalculatedMetrics } from '../../types';

interface ProbabilisticAnalysisProps {
  data: CalculatedMetrics[];
  spikeZones: Record<string, { label: string; min: number; max: number; color: string }>;
}

interface ProbabilityDistribution {
  zone: string;
  color: string;
  probabilities: {
    retracement10: number;
    retracement20: number;
    retracement30: number;
    retracement50: number;
  };
  expectedValue: number;
  variance: number;
  confidenceInterval: {
    lower: number;
    upper: number;
  };
}

export const ProbabilisticAnalysis: React.FC<ProbabilisticAnalysisProps> = ({ data, spikeZones }) => {
  const probabilityData = useMemo((): ProbabilityDistribution[] => {
    if (data.length === 0) return [];

    return Object.values(spikeZones).map(zone => {
      const zoneData = data.filter(d => {
        const spike = (d['Spike %'] || 0) * 100;
        return spike >= zone.min && spike < zone.max;
      });

      if (zoneData.length === 0) {
        return {
          zone: zone.label,
          color: zone.color,
          probabilities: {
            retracement10: 0,
            retracement20: 0,
            retracement30: 0,
            retracement50: 0
          },
          expectedValue: 0,
          variance: 0,
          confidenceInterval: { lower: 0, upper: 0 }
        };
      }

      const retracements = zoneData.map(d => {
        const ret = ((d.Close - d.High) / d.High) * 100;
        return isFinite(ret) ? Math.abs(ret) : 0;
      });

      const calculateProb = (threshold: number) => {
        const count = retracements.filter(r => r >= threshold).length;
        return (count / retracements.length) * 100;
      };

      const mean = retracements.reduce((sum, r) => sum + r, 0) / retracements.length;
      const variance = retracements.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / retracements.length;
      const stdDev = Math.sqrt(variance);

      const z95 = 1.96;
      const marginOfError = z95 * stdDev / Math.sqrt(retracements.length);

      return {
        zone: zone.label,
        color: zone.color,
        probabilities: {
          retracement10: calculateProb(10),
          retracement20: calculateProb(20),
          retracement30: calculateProb(30),
          retracement50: calculateProb(50)
        },
        expectedValue: mean,
        variance,
        confidenceInterval: {
          lower: Math.max(0, mean - marginOfError),
          upper: mean + marginOfError
        }
      };
    });
  }, [data, spikeZones]);

  const overallStats = useMemo(() => {
    if (data.length === 0) {
      return {
        expectedProfit: 0,
        profitVariance: 0,
        winProbability: 0,
        expectedDrawdown: 0
      };
    }

    const retracements = data.map(d => {
      const ret = ((d.Close - d.High) / d.High) * 100;
      return isFinite(ret) ? Math.abs(ret) : 0;
    });

    const expectedProfit = retracements.reduce((sum, r) => sum + r, 0) / retracements.length;
    const profitVariance = retracements.reduce((sum, r) => sum + Math.pow(r - expectedProfit, 2), 0) / retracements.length;
    const winProbability = (data.filter(d => d['Red Close'] === 1).length / data.length) * 100;

    const losses = retracements.map(r => r - expectedProfit).filter(loss => loss < 0);
    const expectedDrawdown = losses.length > 0
      ? Math.abs(losses.reduce((sum, loss) => sum + loss, 0) / losses.length)
      : 0;

    return {
      expectedProfit,
      profitVariance,
      winProbability,
      expectedDrawdown
    };
  }, [data]);

  return (
    <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 sm:p-6">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-6">
        <div className="p-1.5 sm:p-2 bg-blue-400/10 rounded-lg">
          <Target className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
        </div>
        <div>
          <h4 className="text-base sm:text-lg font-semibold text-white">Analisi Probabilistica</h4>
          <p className="text-xs sm:text-sm text-gray-400">
            Probabilità di ritracciamento e statistiche inferenziali
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">Valore Atteso</p>
          <p className="text-2xl font-bold text-green-400">
            {overallStats.expectedProfit.toFixed(2)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">Ritracciamento medio</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">Varianza</p>
          <p className="text-2xl font-bold text-purple-400">
            {overallStats.profitVariance.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 mt-1">Volatilità dei ritorni</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">P(Win)</p>
          <p className="text-2xl font-bold text-blue-400">
            {overallStats.winProbability.toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">Probabilità di vincita</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
          <p className="text-xs text-gray-400 mb-1">Drawdown Atteso</p>
          <p className="text-2xl font-bold text-red-400">
            {overallStats.expectedDrawdown.toFixed(2)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">Perdita media</p>
        </div>
      </div>

      <div className="mb-6">
        <h5 className="text-sm font-medium text-white mb-4">Probabilità di Ritracciamento per Zona</h5>
        <div className="space-y-4">
          {probabilityData.map((zone, index) => (
            <div key={index} className="bg-gray-800/50 border border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: zone.color }}
                  />
                  <h6 className="text-sm font-medium text-white">{zone.zone}</h6>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Valore Atteso</p>
                  <p className="text-lg font-bold text-green-400">
                    {zone.expectedValue.toFixed(2)}%
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div className="text-center">
                  <p className="text-xs text-gray-400 mb-1">≥ 10%</p>
                  <p className="text-lg font-bold text-green-400">
                    {zone.probabilities.retracement10.toFixed(1)}%
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 mb-1">≥ 20%</p>
                  <p className="text-lg font-bold text-yellow-400">
                    {zone.probabilities.retracement20.toFixed(1)}%
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 mb-1">≥ 30%</p>
                  <p className="text-lg font-bold text-orange-400">
                    {zone.probabilities.retracement30.toFixed(1)}%
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 mb-1">≥ 50%</p>
                  <p className="text-lg font-bold text-red-400">
                    {zone.probabilities.retracement50.toFixed(1)}%
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-700 pt-2 mt-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-400">Intervallo di Confidenza 95%:</span>
                  <span className="text-blue-400 font-medium">
                    [{zone.confidenceInterval.lower.toFixed(2)}%, {zone.confidenceInterval.upper.toFixed(2)}%]
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="text-sm font-medium text-blue-400">Interpretazione</h5>
            <ul className="text-xs text-blue-300 mt-2 space-y-1">
              <li>• <strong>Valore Atteso:</strong> Ritracciamento medio che puoi aspettarti</li>
              <li>• <strong>Varianza:</strong> Misura della dispersione dei risultati (alta = più rischio)</li>
              <li>• <strong>P(Win):</strong> Probabilità che il trade sia profittevole (chiusura rossa)</li>
              <li>• <strong>Intervallo di Confidenza:</strong> Range dove il ritracciamento reale cadrà il 95% delle volte</li>
              <li>• Zone con alta probabilità di ritracciamento {'>'} 20% sono ideali per short entry</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
